import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Input, Button, Accordion } from '../common';
import { useExpenseStore } from '../../stores/expenseStore';
import { calculateVATExclusive } from '../../lib/calculations/expenses';
import { useCurrency } from '../../lib/utils/currency';
import { useUnitStore } from '../../stores/unitStore';


interface TonnageExpense {
  tonnage: number;
  unitPrice: number;
  totalAmount: number;
}

interface ExpensesFormProps {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

export function ExpensesForm({ isOpen, onToggle }: ExpensesFormProps = {}) {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const { weightUnit } = useUnitStore();
  const massUnit = weightUnit === 'lb' ? 'lb' : 'ton';
  const { expenses, addExpense, removeExpense, calculateTotalExpenses } = useExpenseStore();

  // Tonnage-based expenses (coal, rice)
  const [coalExpense, setCoalExpense] = useState<TonnageExpense>({
    tonnage: 0,
    unitPrice: 0,
    totalAmount: 0,
  });
  const [riceExpense, setRiceExpense] = useState<TonnageExpense>({
    tonnage: 0,
    unitPrice: 0,
    totalAmount: 0,
  });

  // Utility expenses - single input
  const [electricityAmount, setElectricityAmount] = useState<number>(0);
  const [waterAmount, setWaterAmount] = useState<number>(0);
  const [wasteWaterAmount, setWasteWaterAmount] = useState<number>(0);

  // Biosecurity expenses
  const [disinfectionAmount, setDisinfectionAmount] = useState<number>(0);
  const [medicineAmount, setMedicineAmount] = useState<number>(0);

  // Labor cost
  const [laborAmount, setLaborAmount] = useState<number>(0);

  // VAT rates for each expense type with specific defaults
  const [coalVatRate, setCoalVatRate] = useState<number>(20);
  const [riceVatRate, setRiceVatRate] = useState<number>(20);
  const [electricityVatRate, setElectricityVatRate] = useState<number>(20);
  const [waterVatRate, setWaterVatRate] = useState<number>(1);
  const [wasteWaterVatRate, setWasteWaterVatRate] = useState<number>(10);
  const [disinfectionVatRate, setDisinfectionVatRate] = useState<number>(20);
  const [medicineVatRate, setMedicineVatRate] = useState<number>(0);

  const handleTonnageChange = (
    category: 'coal' | 'rice',
    field: 'tonnage' | 'unitPrice' | 'totalAmount',
    value: string
  ) => {
    const currentExpense = category === 'coal' ? coalExpense : riceExpense;
    const setter = category === 'coal' ? setCoalExpense : setRiceExpense;

    let newExpense = { ...currentExpense };
    
    // Boş string'e izin ver
    if (value === '') {
      newExpense[field] = 0;
      setter(newExpense);
      return;
    }

    const numValue = parseFloat(value);
    
    // Geçersiz sayı ise işleme alma
    if (isNaN(numValue)) {
      return;
    }

    // Smart calculation: when 2 of 3 values are present, calculate the third
    if (field === 'tonnage') {
      newExpense.tonnage = numValue;
      if (newExpense.unitPrice > 0) {
        newExpense.totalAmount = newExpense.tonnage * newExpense.unitPrice;
      }
    } else if (field === 'unitPrice') {
      newExpense.unitPrice = numValue;
      if (newExpense.tonnage > 0) {
        newExpense.totalAmount = newExpense.tonnage * newExpense.unitPrice;
      }
    } else if (field === 'totalAmount') {
      newExpense.totalAmount = numValue;
      if (newExpense.tonnage > 0) {
        newExpense.unitPrice = newExpense.totalAmount / newExpense.tonnage;
      } else if (newExpense.unitPrice > 0) {
        newExpense.tonnage = newExpense.totalAmount / newExpense.unitPrice;
      }
    }

    setter(newExpense);
  };

  const addTonnageExpense = (category: 'coal' | 'rice') => {
    const expense = category === 'coal' ? coalExpense : riceExpense;
    const vatRate = category === 'coal' ? coalVatRate : riceVatRate;
    
    if (expense.totalAmount <= 0) {
      alert(t('expenses.enterValidAmount'));
      return;
    }

    const vatExclusiveAmount = calculateVATExclusive(expense.totalAmount, vatRate);
    const categoryLabel = category === 'coal' ? t('expenses.coal') : t('expenses.rice');
    const description = expense.tonnage > 0 && expense.unitPrice > 0
      ? `${categoryLabel} - ${expense.tonnage.toFixed(2)} ton × ${expense.unitPrice.toFixed(2)} ${symbol}`
      : categoryLabel;

    addExpense({
      category,
      description,
      amount: vatExclusiveAmount,
      vatRate: vatRate,
      tonnage: expense.tonnage,
      unitPrice: expense.unitPrice,
    });

    // Reset form
    if (category === 'coal') {
      setCoalExpense({ tonnage: 0, unitPrice: 0, totalAmount: 0 });
    } else {
      setRiceExpense({ tonnage: 0, unitPrice: 0, totalAmount: 0 });
    }
  };

  const addUtilityExpense = (category: 'electricity' | 'water' | 'wastewater') => {
    let totalAmount: number;
    let vatRate: number;
    let categoryLabel: string;
    
    if (category === 'electricity') {
      totalAmount = electricityAmount;
      vatRate = electricityVatRate;
      categoryLabel = t('expenses.electricity');
    } else if (category === 'water') {
      totalAmount = waterAmount;
      vatRate = waterVatRate;
      categoryLabel = t('expenses.waterUsageLabel');
    } else {
      totalAmount = wasteWaterAmount;
      vatRate = wasteWaterVatRate;
      categoryLabel = t('expenses.wasteWaterLabel');
    }
    
    if (totalAmount <= 0) {
      alert(t('expenses.enterValidAmount'));
      return;
    }

    const vatExclusiveAmount = calculateVATExclusive(totalAmount, vatRate);
    
    // Count existing invoices of this type
    const existingCount = expenses.filter(e => e.category === (category === 'wastewater' ? 'water' : category)).length;

    addExpense({
      category: category === 'wastewater' ? 'water' : category,
      description: `${categoryLabel} ${existingCount + 1}`,
      amount: vatExclusiveAmount,
      vatRate: vatRate,
    });

    // Reset input
    if (category === 'electricity') {
      setElectricityAmount(0);
    } else if (category === 'water') {
      setWaterAmount(0);
    } else {
      setWasteWaterAmount(0);
    }
  };

  const addWaterBill = () => {
    // Validate both amounts
    if (waterAmount <= 0 && wasteWaterAmount <= 0) {
      alert(t('expenses.enterWaterAmount'));
      return;
    }

    const existingCount = expenses.filter(e => e.category === 'water').length;

    // Add water bill if amount > 0
    if (waterAmount > 0) {
      const vatExclusiveAmount = calculateVATExclusive(waterAmount, waterVatRate);
      addExpense({
        category: 'water',
        description: `${t('expenses.waterUsageLabel')} ${existingCount + 1}`,
        amount: vatExclusiveAmount,
        vatRate: waterVatRate,
      });
    }

    // Add waste water bill if amount > 0
    if (wasteWaterAmount > 0) {
      const vatExclusiveAmount = calculateVATExclusive(wasteWaterAmount, wasteWaterVatRate);
      addExpense({
        category: 'water',
        description: `${t('expenses.wasteWaterLabel')} ${existingCount + 1}`,
        amount: vatExclusiveAmount,
        vatRate: wasteWaterVatRate,
      });
    }

    // Reset inputs
    setWaterAmount(0);
    setWasteWaterAmount(0);
  };

  const addBiosecurityExpense = (category: 'disinfection' | 'medicine') => {
    const totalAmount = category === 'disinfection' ? disinfectionAmount : medicineAmount;
    const vatRate = category === 'disinfection' ? disinfectionVatRate : medicineVatRate;
    
    if (totalAmount <= 0) {
      alert(t('expenses.enterValidAmount'));
      return;
    }

    const vatExclusiveAmount = calculateVATExclusive(totalAmount, vatRate);
    const categoryLabel = category === 'disinfection' ? t('expenses.disinfection') : t('expenses.medicine');

    addExpense({
      category,
      description: categoryLabel,
      amount: vatExclusiveAmount,
      vatRate: vatRate,
    });

    // Reset input
    if (category === 'disinfection') {
      setDisinfectionAmount(0);
    } else {
      setMedicineAmount(0);
    }
  };

  const addLaborExpense = () => {
    if (laborAmount <= 0) {
      alert(t('expenses.enterValidAmount'));
      return;
    }

    const vatRate = 0; // No VAT on labor

    addExpense({
      category: 'labor',
      description: t('expenses.labor'),
      amount: laborAmount,
      vatRate,
    });

    setLaborAmount(0);
  };



  const totalExpenses = calculateTotalExpenses();

  return (
    <Accordion 
      title={t('expenses.title')}
      defaultOpen={false} 
      isOpen={isOpen} 
      onToggle={onToggle}
    >
      <div className="space-y-6">
        {/* VAT Rate Info */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>{t('expenses.vatCalculation')}</strong> {t('expenses.vatRateDescription')}
              </p>
            </div>
          </div>
        </div>

        {/* Tonnage-based Expenses */}
        <Card title={t('expenses.tonnageBased')} padding="sm">
          <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              💡 {t('expenses.vatInfoShort')}
            </p>
          </div>
          <div className="space-y-4">
            {/* Coal */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('expenses.coal')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <Input
                  label={t('expenses.quantity', { massUnit })}
                  type="number"
                  min="0"
                  step="0.01"
                  value={coalExpense.tonnage ?? ''}
                  onChange={(e) => handleTonnageChange('coal', 'tonnage', e.target.value)}
                  placeholder={t('expenses.quantityPlaceholder')}
                />
                <Input
                  label={t('expenses.unitPrice', { symbol, massUnit })}
                  type="number"
                  min="0"
                  step="0.01"
                  value={coalExpense.unitPrice ?? ''}
                  onChange={(e) => handleTonnageChange('coal', 'unitPrice', e.target.value)}
                  placeholder={t('expenses.unitPricePlaceholder')}
                />
                <Input
                  label={t('expenses.totalWithVAT')}
                  type="number"
                  min="0"
                  step="0.01"
                  value={coalExpense.totalAmount ?? ''}
                  onChange={(e) => handleTonnageChange('coal', 'totalAmount', e.target.value)}
                  placeholder={t('expenses.totalPlaceholder')}
                />
                <Input
                  label={`${t('expenses.vatRate')} (%)`}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={coalVatRate}
                  onChange={(e) => setCoalVatRate(parseFloat(e.target.value) || 0)}
                  placeholder="20"
                />
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => addTonnageExpense('coal')}
                    fullWidth
                  >
                    {t('common.add')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Rice */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('expenses.rice')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <Input
                  label={t('expenses.quantity', { massUnit })}
                  type="number"
                  min="0"
                  step="0.01"
                  value={riceExpense.tonnage ?? ''}
                  onChange={(e) => handleTonnageChange('rice', 'tonnage', e.target.value)}
                  placeholder={t('expenses.quantityPlaceholder')}
                />
                <Input
                  label={t('expenses.unitPrice', { symbol, massUnit })}
                  type="number"
                  min="0"
                  step="0.01"
                  value={riceExpense.unitPrice ?? ''}
                  onChange={(e) => handleTonnageChange('rice', 'unitPrice', e.target.value)}
                  placeholder={t('expenses.unitPricePlaceholder')}
                />
                <Input
                  label={t('expenses.totalWithVAT')}
                  type="number"
                  min="0"
                  step="0.01"
                  value={riceExpense.totalAmount ?? ''}
                  onChange={(e) => handleTonnageChange('rice', 'totalAmount', e.target.value)}
                  placeholder={t('expenses.totalPlaceholder')}
                />
                <Input
                  label={`${t('expenses.vatRate')} (%)`}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={riceVatRate}
                  onChange={(e) => setRiceVatRate(parseFloat(e.target.value) || 0)}
                  placeholder="20"
                />
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => addTonnageExpense('rice')}
                    fullWidth
                  >
                    {t('common.add')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Utility Bills */}
        <Card title={t('expenses.utilities')} padding="sm">
          <div className="space-y-4">
            {/* Electricity */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('expenses.electricity')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <div className="md:col-span-2">
                  <Input
                    label={t('expenses.amount', { symbol })}
                    type="number"
                    min="0"
                    step="0.01"
                    value={electricityAmount ?? ''}
                    onChange={(e) => setElectricityAmount(parseFloat(e.target.value) || 0)}
                    placeholder={t('expenses.electricityPlaceholder')}
                    helperText={t('expenses.multipleInvoices')}
                  />
                </div>
                <Input
                  label={`${t('expenses.vatRate')} (%)`}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={electricityVatRate}
                  onChange={(e) => setElectricityVatRate(parseFloat(e.target.value) || 0)}
                  placeholder="20"
                />
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => addUtilityExpense('electricity')}
                    fullWidth
                  >
                    {t('common.add')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Water Bill */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('expenses.waterBill')}
              </h4>
              <div className="space-y-3">
                {/* Water Usage */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div className="md:col-span-2">
                    <Input
                      label={t('expenses.waterUsageAmount', { symbol })}
                      type="number"
                      min="0"
                      step="0.01"
                      value={waterAmount ?? ''}
                      onChange={(e) => setWaterAmount(parseFloat(e.target.value) || 0)}
                      placeholder={t('expenses.waterUsagePlaceholder')}
                    />
                  </div>
                  <Input
                    label={`${t('expenses.vatRate')} (%)`}
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={waterVatRate}
                    onChange={(e) => setWaterVatRate(parseFloat(e.target.value) || 0)}
                    placeholder="1"
                  />
                  <div></div>
                </div>
                
                {/* Waste Water */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div className="md:col-span-2">
                    <Input
                      label={t('expenses.wasteWaterAmount', { symbol })}
                      type="number"
                      min="0"
                      step="0.01"
                      value={wasteWaterAmount ?? ''}
                      onChange={(e) => setWasteWaterAmount(parseFloat(e.target.value) || 0)}
                      placeholder={t('expenses.wasteWaterPlaceholder')}
                      helperText={t('expenses.multipleInvoices')}
                    />
                  </div>
                  <Input
                    label={`${t('expenses.vatRate')} (%)`}
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={wasteWaterVatRate}
                    onChange={(e) => setWasteWaterVatRate(parseFloat(e.target.value) || 0)}
                    placeholder="10"
                  />
                  <div className="flex items-end">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={addWaterBill}
                      fullWidth
                    >
                      {t('common.add')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Biosecurity Expenses */}
        <Card title={t('expenses.biosecurity')} padding="sm">
          <div className="space-y-4">
            {/* Disinfection */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('expenses.disinfection')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <div className="md:col-span-2">
                  <Input
                    label={t('expenses.amount', { symbol })}
                    type="number"
                    min="0"
                    step="0.01"
                    value={disinfectionAmount ?? ''}
                    onChange={(e) => setDisinfectionAmount(parseFloat(e.target.value) || 0)}
                    placeholder={t('expenses.disinfectionPlaceholder')}
                  />
                </div>
                <Input
                  label={`${t('expenses.vatRate')} (%)`}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={disinfectionVatRate}
                  onChange={(e) => setDisinfectionVatRate(parseFloat(e.target.value) || 0)}
                  placeholder="20"
                />
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => addBiosecurityExpense('disinfection')}
                    fullWidth
                  >
                    {t('common.add')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Medicine */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('expenses.medicine')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <div className="md:col-span-2">
                  <Input
                    label={t('expenses.amount', { symbol })}
                    type="number"
                    min="0"
                    step="0.01"
                    value={medicineAmount ?? ''}
                    onChange={(e) => setMedicineAmount(parseFloat(e.target.value) || 0)}
                    placeholder={t('expenses.medicinePlaceholder')}
                  />
                </div>
                <Input
                  label={`${t('expenses.vatRate')} (%)`}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={medicineVatRate}
                  onChange={(e) => setMedicineVatRate(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                />
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => addBiosecurityExpense('medicine')}
                    fullWidth
                  >
                    {t('common.add')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Labor */}
        <Card title={t('expenses.labor')} padding="sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="md:col-span-2">
              <Input
                label={t('expenses.laborCost', { symbol })}
                type="number"
                min="0"
                step="0.01"
                value={laborAmount ?? ''}
                onChange={(e) => setLaborAmount(parseFloat(e.target.value) || 0)}
                placeholder={t('expenses.laborPlaceholder')}
                helperText={t('expenses.noVAT')}
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="primary"
                size="sm"
                onClick={addLaborExpense}
                fullWidth
              >
                {t('common.add')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Expense List */}
        {expenses.length > 0 && (
          <Card title={t('expenses.addedExpenses')} padding="sm">
            <div className="space-y-2">
              {expenses.map((expense) => (
                <div
                  key={expense.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-md"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {expense.description}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="text-green-600 dark:text-green-400">
                        {expense.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {symbol} ({t('expenses.vatExclusive')})
                      </span>
                      {expense.vatRate > 0 && (
                        <>
                          {' ← '}
                          <span className="font-semibold">
                            {(expense.amount * (1 + expense.vatRate / 100)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {symbol} ({t('expenses.vatInclusive')})
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => removeExpense(expense.id)}
                  >
                    {t('common.delete')}
                  </Button>
                </div>
              ))}



              {/* Total */}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center font-semibold text-lg">
                  <span className="text-gray-900 dark:text-gray-100">
                    {t('expenses.totalExpenses')}:
                  </span>
                  <span className="text-green-600 dark:text-green-400">
                    {totalExpenses.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {symbol}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Info Box */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>{t('expenses.vatCalculation')}</strong> {t('expenses.vatInfo')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Accordion>
  );
}
